import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Alert, Button, Card, Icon, Spinner, StatusPill, Textarea, useToast } from '../../components/ui';
import {
  CycleSelector, LicenseActivationCard, PaymentMethodSelector, PixChargeCard, PlanCards,
  type BillingCycle, type PaymentMethod, type PixCharge, type PlanView
} from '../../components/PlanPicker';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { brl, cycleLabel, datePt, dateTimePt, subscriptionLabel, trialDaysLeft } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';
import { CurrencyNote } from '../../components/LanguageSelect';

interface Subscription {
  id: string;
  planKey: string;
  planName: string;
  billingCycle: string;
  status: string;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  provider: string | null;
  maxProfessionals: number;
}

interface SubsResponse {
  subscription: Subscription | null;
  events: { event: string; data: string | null; created_at: string }[];
  payments: { kind: string; provider: string; amountCents: number; status: string; paidAt: string | null; createdAt: string }[];
  paymentProvider: string;
  paymentConfigured: boolean;
  checkoutMethods: PaymentMethod[];
  pixEnabled: boolean;
}

interface CheckoutResponse {
  mode: string;
  transactionId?: string;
  checkoutUrl?: string;
  message?: string;
  note?: string;
  pix?: { payload: string; reference: string; amountCents: number; pixKey: string };
  qrDataUrl?: string;
}

interface Fail { status: number; message: string }

function toFail(err: unknown): Fail {
  if (err instanceof ApiError) return { status: err.status, message: err.message };
  return { status: 0, message: err instanceof Error ? err.message : 'Não foi possível concluir a operação.' };
}

const PROVIDER_LABEL: Record<string, string> = {
  asaas: 'Asaas',
  mercadopago: 'Mercado Pago',
  stripe: 'Stripe',
  pix: 'Pix manual',
  license: 'Licença',
  none: '—'
};

function providerLabel(provider: string | null | undefined): string {
  if (!provider) return '—';
  const label = PROVIDER_LABEL[provider];
  return label ? tr(label) : provider;
}

function statusTone(status: string): 'positive' | 'warning' | 'danger' | 'neutral' {
  if (status === 'active') return 'positive';
  if (status === 'trialing') return 'warning';
  if (status === 'suspended' || status === 'past_due' || status === 'canceled') return 'danger';
  return 'neutral';
}

export function PanelPlanPage(): JSX.Element {
  usePageMeta('Meu plano', 'Assinatura, teste gratuito e planos AgendaPro.', '/painel/plano');
  const toast = useToast();
  const { active, refresh } = useAuth();
  const bid = active?.businessId ?? '';
  const isOwner = active?.role === 'owner';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Fail | null>(null);
  const [plans, setPlans] = useState<PlanView[]>([]);
  const [, setPaymentConfigured] = useState<boolean | null>(null);
  const [checkoutMethods, setCheckoutMethods] = useState<PaymentMethod[]>([]);
  const [paymentProvider, setPaymentProvider] = useState('none');
  const [pixEnabled, setPixEnabled] = useState(false);
  const [sub, setSub] = useState<Subscription | null>(null);
  const [events, setEvents] = useState<SubsResponse['events']>([]);
  const [payments, setPayments] = useState<SubsResponse['payments']>([]);
  const [pix, setPix] = useState<PixCharge | null>(null);
  const [pixCardOpen, setPixCardOpen] = useState(true);

  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [method, setMethod] = useState<PaymentMethod>('card');
  const [busy, setBusy] = useState(false);
  const [checkoutNotice, setCheckoutNotice] = useState<{ tone: 'warning' | 'info'; text: string } | null>(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [immediate, setImmediate] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const loadPix = useCallback(async (businessId: string): Promise<void> => {
    try {
      setPix(await api.get<PixCharge>(`/api/subscriptions/${businessId}/pix`));
    } catch {
      setPix(null);
    }
  }, []);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const p = await api.get<{
        paymentConfigured: boolean;
        paymentProvider: string;
        checkoutMethods: PaymentMethod[];
        pixEnabled: boolean;
        plans: PlanView[];
      }>('/api/plans');
      setPlans(p.plans);
      setPaymentConfigured(p.paymentConfigured);
      setCheckoutMethods(p.checkoutMethods ?? []);
      setPaymentProvider(p.paymentProvider ?? 'none');
      setPixEnabled(p.pixEnabled);
      if (bid) {
        const s = await api.get<SubsResponse>(`/api/subscriptions/${bid}`);
        setSub(s.subscription);
        setEvents(s.events);
        setPayments(s.payments);
        setPaymentConfigured(s.paymentConfigured);
        setCheckoutMethods(s.checkoutMethods ?? p.checkoutMethods ?? []);
        setPaymentProvider(s.paymentProvider ?? p.paymentProvider ?? 'none');
        setPixEnabled(s.pixEnabled);
        await loadPix(bid);
      }
    } catch (err) {
      setError(toFail(err));
    } finally {
      setLoading(false);
    }
  }, [bid, loadPix]);

  useEffect(() => {
    void load();
  }, [load]);

  const daysLeft = sub ? trialDaysLeft(sub.trialEndsAt) : null;

  async function checkout(planKey: string): Promise<void> {
    setBusy(true);
    setError(null);
    setCheckoutNotice(null);
    try {
      const res = await api.post<CheckoutResponse>(`/api/subscriptions/${bid}/checkout`, { planKey, cycle, method });
      if (res.mode === 'pix') {
        setPixCardOpen(true);
        toast('Cobrança Pix gerada. Pague e envie o comprovante.', 'success');
        await load();
        await refresh();
        return;
      }
      if (res.mode === 'hosted_checkout' && res.checkoutUrl) {
        setCheckoutNotice({
          tone: 'info',
          text: tr('{note} Você será redirecionado ao ambiente seguro de pagamento em instantes…', { note: res.note ?? '' })
        });
        toast(tr('Abrindo ambiente de pagamento…'), 'info');
        window.setTimeout(() => { window.location.href = res.checkoutUrl as string; }, 900);
      } else if (res.mode === 'pending_configuration') {
        setCheckoutNotice({
          tone: 'warning',
          text: `${res.message ?? tr('A cobrança online ainda não foi configurada pelo administrador.')} ${tr('Seu plano NÃO foi ativado nem cobrado agora; ele fica aguardando a configuração do provedor de pagamento ou do Pix.')}`
        });
        toast(tr('Pedido registrado. Cobrança online indisponível.'), 'info');
        await load();
        await refresh();
      } else {
        setCheckoutNotice({ tone: 'info', text: tr('Checkout iniciado. A ativação ocorre somente após a confirmação do provedor.') });
        await load();
        await refresh();
      }
    } catch (err) {
      setError(toFail(err));
    } finally {
      setBusy(false);
    }
  }

  async function cancel(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ message: string }>(`/api/subscriptions/${bid}/cancel`, {
        immediate,
        reason: cancelReason.trim() || undefined
      });
      toast(res.message, 'success');
      setCancelOpen(false);
      setImmediate(false);
      setCancelReason('');
      await load();
      await refresh();
    } catch (err) {
      setError(toFail(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{tr("Meu plano")}</h1>
        <p className="text-sm text-muted">{tr("Acompanhe seu teste gratuito, assinatura e cobrança. Esta tela funciona mesmo quando o acesso operacional está bloqueado.")}</p>
        <CurrencyNote className="mt-1" />
      </div>

      {error ? (
        <Alert tone={error.status === 403 ? 'warning' : 'danger'} title={error.status === 403 ? tr('Permissão insuficiente') : tr('Erro ao carregar')}>
          <p>{error.message}</p>
        </Alert>
      ) : null}
      {checkoutNotice ? <Alert tone={checkoutNotice.tone} title={checkoutNotice.tone === 'warning' ? tr('Cobrança online ainda indisponível') : tr('Pagamento')}>{checkoutNotice.text}</Alert> : null}

      {!bid ? (
        <Card as="section"><p className="text-sm">{tr("Nenhum estabelecimento vinculado à sua conta. Crie ou acesse um negócio profissional para gerenciar a assinatura.")}</p></Card>
      ) : loading ? (
        <Card><Spinner label={tr("Carregando plano…")} /></Card>
      ) : (
        <>
          <Card as="section">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm text-muted">{tr("Plano atual")}</p>
                {sub ? (
                  <>
                    <p className="text-xl font-semibold">{sub.planName}</p>
                    <p className="text-sm text-muted">
                      {tr("Cobrança")} {cycleLabel(sub.billingCycle).toLowerCase()} {tr("· até")} {sub.maxProfessionals} {tr("profissional(is) · provedor:")} {providerLabel(sub.provider)}
                    </p>
                  </>
                ) : (
                  <p className="text-xl font-semibold">{tr("Sem assinatura ativa")}</p>
                )}
              </div>
              <div className="text-right">
                {sub ? <StatusPill tone={statusTone(sub.status)}>{subscriptionLabel(sub.status)}</StatusPill> : <StatusPill tone="danger">{tr("Não iniciado")}</StatusPill>}
                {sub && (
                  <dl className="mt-2 space-y-0.5 text-sm">
                    {sub.status === 'trialing' && (
                      <>
                        <Row k="Início do teste" v={datePt(sub.trialStartedAt)} />
                        <Row k="Término do teste" v={datePt(sub.trialEndsAt)} />
                        <Row k="Dias restantes" v={daysLeft === null ? '—' : `${daysLeft} dia(s)`} />
                      </>
                    )}
                    {sub.currentPeriodStart ? <Row k="Período atual" v={`${datePt(sub.currentPeriodStart)} – ${datePt(sub.currentPeriodEnd)}`} /> : null}
                    {sub.cancelAtPeriodEnd ? <Row k="Cancelamento" v="Agendado para o fim do período" /> : null}
                  </dl>
                )}
              </div>
            </div>

            {sub?.status === 'canceled' || sub?.status === 'expired' || !sub ? (
              <Alert tone="warning" title={tr("Acesso operacional bloqueado")}>
                {tr("Para voltar a usar agenda e relatórios, ative um código de licença ou contrate um plano abaixo.")}
              </Alert>
            ) : null}
          </Card>

          {isOwner && sub?.status !== 'suspended' ? (
            <LicenseActivationCard businessId={bid} onActivated={async () => { await load(); await refresh(); }} />
          ) : null}

          {pix && pixCardOpen ? (
            <PixChargeCard charge={pix} onDismiss={() => setPixCardOpen(false)} />
          ) : pix ? (
            <Card as="section" className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm">{tr("Há uma cobrança Pix pendente de")} <strong>{brl(pix.amountCents)}</strong> {tr("(referência")} {pix.reference}).</p>
              <Button variant="secondary" onClick={() => setPixCardOpen(true)}>{tr("Mostrar QR Code do Pix")}</Button>
            </Card>
          ) : null}

          {!isOwner ? (
            <Alert tone="info" title={tr("Somente o proprietário")}>{tr("Alterar plano ou cancelar assinatura é permitido apenas ao proprietário do estabelecimento.")}</Alert>
          ) : null}

          {checkoutMethods.length === 0 && !pixEnabled ? (
            <Alert tone="warning" title={tr("Cobrança online não configurada")}>
              {tr("A integração de pagamentos ainda não foi configurada pelo administrador da AgendaPro. Você pode solicitar planos, mas a")} <strong>{tr("cobrança não será processada e o plano não será ativado até que o provedor esteja configurado")}</strong>{tr(". Não considere nenhuma contratação concluída ao retornar desta tela.")}
            </Alert>
          ) : null}

          {checkoutMethods.length === 0 && pixEnabled && !pix ? (
            <Alert tone="info" title={tr("Pagamento por Pix")}>
              {tr("As cobranças de plano são emitidas como")} <strong>{tr("Pix com QR Code e copia e cola")}</strong>{tr(", creditado na chave da plataforma. O código aparece na seção \"Pague com Pix\" acima, logo depois de clicar em pagar. A liberação do acesso é feita manualmente pela administração ao confirmar o recebimento — nada é ativado sozinho.")}
            </Alert>
          ) : null}

          {checkoutMethods.length > 0 ? (
            <Alert tone="info" title={tr("Pagamento pelo gateway")}>
              {tr("O pagamento é processado pelo")} <strong>{providerLabel(paymentProvider)}</strong> {tr("em ambiente seguro; os dados do cartão não passam pela AgendaPro.")}
              {checkoutMethods.includes('card') ? ' No cartão, a assinatura se renova automaticamente a cada ciclo.' : ''}
              {checkoutMethods.includes('pix') ? ' No Pix, o valor é único para o ciclo escolhido e não se renova sozinho.' : ''}
              {' '}{tr("A liberação do acesso é automática após a confirmação do pagamento — voltar para esta página não ativa nada por si só.")}
            </Alert>
          ) : null}

          <Card as="section">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">{tr("Planos")}</h2>
              <div className="flex flex-wrap items-center gap-2">
                <PaymentMethodSelector method={method} methods={checkoutMethods} onChange={setMethod} />
                <CycleSelector cycle={cycle} onChange={setCycle} />
              </div>
            </div>
            <PlanCards
              plans={plans}
              cycle={cycle}
              method={method}
              checkoutMethods={checkoutMethods}
              busy={busy}
              pixEnabled={pixEnabled}
              currentPlanKey={sub && (sub.status === 'active' || sub.status === 'trialing') ? sub.planKey : undefined}
              canChoose={isOwner}
              onChoose={(planKey) => void checkout(planKey)}
            />
            {checkoutMethods.length === 0 && !pixEnabled ? (
              <p className="nl-hint mt-3">{tr("Ao clicar em assinar com a cobrança online desativada, o pedido fica pendente de configuração do provedor — nenhuma cobrança é feita nem o plano é ativado.")}</p>
            ) : null}
            {checkoutMethods.length === 0 && pixEnabled ? (
              <p className="nl-hint mt-3">{tr("Ao clicar, o QR Code e o Pix copia e cola da cobrança aparecem na seção \"Pague com Pix\", logo acima. Guarde a referência: ela é o que permite à administração conferir o seu comprovante e liberar o acesso.")}</p>
            ) : null}
            {checkoutMethods.length > 0 ? (
              <p className="nl-hint mt-3">
                {method === 'card'
                  ? 'No cartão, o valor é cobrado agora e renovado automaticamente no fim de cada ciclo. O cancelamento pode ser feito nesta tela.'
                  : 'No Pix, o valor vale para o ciclo escolhido e não se renova sozinho. Ao final do período, gere um novo Pix para continuar com o acesso.'}
              </p>
            ) : null}
          </Card>

          {isOwner && sub && (sub.status === 'active' || sub.status === 'trialing') && !sub.cancelAtPeriodEnd ? (
            <Card as="section">
              <h2 className="mb-1 text-lg font-semibold">{tr("Cancelar assinatura")}</h2>
              <p className="mb-3 text-sm text-muted">{tr("Você pode cancelar imediatamente ou manter o acesso até o fim do período já pago.")}</p>
              <Button variant="secondary" onClick={() => setCancelOpen(true)}>{tr("Cancelar assinatura")}</Button>
            </Card>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <Card as="section">
              <h2 className="mb-3 text-lg font-semibold">{tr("Transações")}</h2>
              {payments.length === 0 ? <p className="text-sm text-muted">{tr("Nenhuma transação registrada.")}</p> : (
                <ul className="space-y-2 text-sm">
                  {payments.slice(0, 10).map((t, i) => (
                    <li key={i} className="flex justify-between gap-2 border-b border-line pb-1.5">
                      <span>{t.kind === 'trial' ? tr('Teste gratuito') : tr('Cobrança')} · {providerLabel(t.provider)}</span>
                      <span className="text-muted">{brl(t.amountCents)} · {t.status} · {dateTimePt(t.paidAt ?? t.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card as="section">
              <h2 className="mb-3 text-lg font-semibold">{tr("Histórico da assinatura")}</h2>
              {events.length === 0 ? <p className="text-sm text-muted">{tr("Sem eventos.")}</p> : (
                <ul className="space-y-1.5 text-sm">
                  {events.slice(0, 10).map((e, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span>{e.event}</span>
                      <span className="text-muted">{dateTimePt(e.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}

      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title={tr("Cancelar assinatura")}>
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={immediate} onChange={(e) => setImmediate(e.target.checked)} />
            {tr("Cancelar imediatamente (senão, mantém o acesso até o fim do período pago)")}
          </label>
          <label className="block">
            <span className="nl-label">{tr("Motivo (opcional)")}</span>
            <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} maxLength={300} />
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCancelOpen(false)}>{tr("Voltar")}</Button>
            <Button variant="danger" loading={busy} onClick={() => void cancel()}>{tr("Confirmar cancelamento")}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted">{tr(k)}</dt>
      <dd>{v}</dd>
    </div>
  );
}

function Modal({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }): JSX.Element | null {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} className="w-full max-w-md rounded-3xl bg-card p-5 shadow-lg" onMouseDown={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-xl">{title}</h2>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-surface" aria-label={tr("Fechar")}><Icon name="close" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
